<div class="access-matrix mt-2 px-2" [formGroup]="formGroup">
  <!-- Controls: Expand/Collapse & Stats Counter -->
  <div
    class="access-matrix__controls mb-2 flex flex-col md:flex-row gap-4 justify-end ms-auto"
  >
    <div class="access-matrix__actions flex gap-2">
      <button
        *ngIf="!allExpanded"
        class="access-matrix__action-btn px-4 py-2 text-2xs font-semibold rounded border border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 transition flex items-center"
        type="button"
        (click)="expandAll()"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="mr-2"
        >
          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
        Expand All
      </button>
      <button
        *ngIf="allExpanded"
        class="access-matrix__action-btn px-4 py-2 text-2xs font-semibold rounded border border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 transition flex items-center"
        type="button"
        (click)="collapseAll()"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="mr-2"
        >
          <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" />
        </svg>
        Collapse All
      </button>
    </div>
  </div>

  <div
    class="access-matrix__table rounded-lg border border-gray-800 overflow-hidden bg-[#0d121f]"
    role="table"
    [attr.aria-label]="fields?.displayName"
  >
    <!-- Header -->
    <div
      class="access-matrix__header bg-[#121824] border-b border-gray-800"
      role="row"
    >
      <div class="access-matrix__toggle-heading" role="columnheader"></div>
      <div
        class="access-matrix__module-heading text-2xs font-semibold text-gray-400 uppercase tracking-wide flex items-center"
        role="columnheader"
      >
        Module / Page
      </div>
      <div
        class="access-matrix__access-heading text-2xs font-bold uppercase tracking-wide flex flex-col items-center justify-center gap-0.5"
        [class]="'access-matrix__access-heading--' + level.value"
        role="columnheader"
        *ngFor="let level of accessLevels; trackBy: trackByAccessValue"
      >
        <span>{{ level.label }}</span>
        <span class="text-3xs font-medium opacity-75"
          >({{ formatCount(getAccessCount(level.value)) }})</span
        >
      </div>
    </div>

    <!-- Platform + module + child rows -->
    <ng-container
      *ngFor="let platform of platforms; trackBy: trackByPlatformId"
    >
      <!-- Platform row -->
      <div
        class="access-matrix__row access-matrix__row--parent border-b border-gray-800/60"
        role="row"
      >
        <button
          class="access-matrix__toggle"
          type="button"
          role="cell"
          [class.access-matrix__toggle--hidden]="!hasModules(platform)"
          [attr.aria-expanded]="isExpanded(platform)"
          [attr.aria-label]="
            isExpanded(platform)
              ? 'Collapse ' + platform.label
              : 'Expand ' + platform.label
          "
          (click)="toggleRow(platform)"
        >
          <span
            class="access-matrix__chevron"
            [class.access-matrix__chevron--open]="isExpanded(platform)"
          ></span>
        </button>

        <div
          class="access-matrix__module"
          role="cell"
          [class.access-matrix__module--accordion]="hasModules(platform)"
          (click)="toggleRow(platform)"
        >
          <span
            class="menu-icon text-blue-500 w-[18px] h-[18px] shrink-0 flex items-center justify-center"
            [innerHTML]="getIcon(platform.icon)"
          ></span>
          <span
            class="access-matrix__module-label text-gray-100 text-xs font-semibold"
          >
            {{ platform.label }}
          </span>
        </div>

        <label
          class="access-matrix__radio-cell"
          role="cell"
          *ngFor="let level of accessLevels; trackBy: trackByAccessValue"
          [attr.aria-label]="platform.label + ' ' + level.label"
        >
          <input
            class="access-matrix__radio"
            [class]="'access-matrix__radio--' + level.value"
            type="radio"
            [name]="fieldName + '-' + platform._id"
            [value]="level.value"
            [checked]="isPlatformSelected(platform, level.value)"
            (click)="selectPlatformAccess($event, platform, level.value)"
          />
        </label>
      </div>

      <!-- Module rows -->
      <ng-container *ngIf="hasModules(platform) && isExpanded(platform)">
        <ng-container
          *ngFor="
            let module of platform.children;
            trackBy: trackByModuleId;
            last as isLastModule
          "
        >
          <!-- Module row -->
          <div
            class="access-matrix__row access-matrix__row--child border-b border-gray-800/40"
            [class.access-matrix__row--last-module]="
              isLastModule && !hasChildren(module)
            "
            role="row"
          >
            <div class="access-matrix__toggle-placeholder" role="cell"></div>

            <div
              class="access-matrix__module access-matrix__module--child"
              role="cell"
            >
              <span class="access-matrix__child-line shrink-0"></span>
              <span
                class="access-matrix__child-label text-gray-300 text-2sm font-medium"
              >
                {{ module.label }}
              </span>
            </div>

            <label
              class="access-matrix__radio-cell"
              role="cell"
              *ngFor="let level of accessLevels; trackBy: trackByAccessValue"
              [attr.aria-label]="module.label + ' ' + level.label"
            >
              <input
                class="access-matrix__radio"
                [class]="'access-matrix__radio--' + level.value"
                type="radio"
                [name]="fieldName + '-' + platform._id + '-' + module._id"
                [value]="level.value"
                [checked]="isModuleSelected(platform, module, level.value)"
                (click)="
                  selectModuleAccess($event, platform, module, level.value)
                "
              />
            </label>
          </div>

          <!-- Child rows -->
          <ng-container *ngIf="hasChildren(module)">
            <div
              class="access-matrix__row access-matrix__row--subchild border-b border-gray-800/40"
              [class.access-matrix__row--last-subchild]="
                isLastModule && isLastChild
              "
              role="row"
              *ngFor="
                let child of module.children;
                trackBy: trackByChildId;
                last as isLastChild
              "
            >
              <div class="access-matrix__toggle-placeholder" role="cell"></div>

              <div
                class="access-matrix__module access-matrix__module--subchild"
                role="cell"
              >
                <span class="access-matrix__subchild-line shrink-0"></span>
                <span
                  class="access-matrix__child-label text-gray-400 text-2sm font-medium"
                >
                  {{ child.label }}
                </span>
              </div>

              <label
                class="access-matrix__radio-cell"
                role="cell"
                *ngFor="let level of accessLevels; trackBy: trackByAccessValue"
                [attr.aria-label]="child.label + ' ' + level.label"
              >
                <input
                  class="access-matrix__radio"
                  [class]="'access-matrix__radio--' + level.value"
                  type="radio"
                  [name]="
                    fieldName +
                    '-' +
                    platform._id +
                    '-' +
                    module._id +
                    '-' +
                    child._id
                  "
                  [value]="level.value"
                  [checked]="
                    isChildSelected(platform, module, child, level.value)
                  "
                  (click)="
                    selectChildAccess(
                      $event,
                      platform,
                      module,
                      child,
                      level.value
                    )
                  "
                />
              </label>
            </div>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>
  </div>
</div>
